import { useState } from "react";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { http, errText } from "@/lib/api";
import { Field } from "@/components/tools/BirthFields";
import { FormError, PageShell } from "@/components/site/PageShell";

const EMPTY = { name: "", phone: "", email: "", birth_date: "", birth_time: "", birth_place: "", service: "", mode: "phone", preferred_date: "", preferred_time: "", questions: "" };
const REQUIRED = ["name", "phone", "birth_date", "birth_place", "service", "preferred_date", "preferred_time"];

const waLink = (f, t) => {
  const lines = [
    "Namaste Pandit Ajay Sagar Shukla Ji, I have sent a consultation request.",
    `${t.common.name}: ${f.name}`, `${t.book.phone}: ${f.phone}`,
    `${t.common.date}: ${f.birth_date} ${f.birth_time || ""}`, `${t.common.place}: ${f.birth_place}`,
    `${t.book.service}: ${f.service}`, `${t.book.modes[f.mode]}`,
    `${t.book.prefDate}: ${f.preferred_date} ${f.preferred_time}`,
  ];
  if (f.questions) lines.push(f.questions);
  return `https://wa.me/919458233787?text=${encodeURIComponent(lines.join("\n"))}`;
};

const Success = ({ t, form, reset }) => (
  <div className="card p-8 sm:p-12 max-w-2xl" data-testid="book-success">
    <CheckCircle2 className="w-10 h-10 text-emerald-700" />
    <h2 className="mt-5 display text-4xl">{t.book.successTitle}</h2>
    <p className="mt-3 text-[color:var(--muted)]">{t.book.successText}</p>
    <div className="mt-8 flex flex-wrap gap-3">
      <a data-testid="book-whatsapp-btn" href={waLink(form, t)} target="_blank" rel="noreferrer" className="pill-solid px-7 py-3.5 text-sm"><MessageCircle className="w-4 h-4 mr-2" />{t.book.sendWhatsapp}</a>
      <button data-testid="book-another-btn" onClick={reset} className="pill-ghost px-6 py-3.5 text-sm">{t.book.another}</button>
    </div>
  </div>
);

export default function BookPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState({ loading: false, error: "", done: false });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const services = [...t.consult.items, ...t.yagya.items].map((s) => s.title);
  const submit = async (e) => {
    e.preventDefault();
    if (REQUIRED.some((k) => !form[k].trim())) { setState({ loading: false, error: t.book.required, done: false }); return; }
    setState({ loading: true, error: "", done: false });
    try {
      await http.post("/bookings", { ...form, lang });
      setState({ loading: false, error: "", done: true });
    } catch (err) {
      setState({ loading: false, error: errText(err, t.common.error), done: false });
    }
  };
  const input = (k, label, props = {}) => (
    <Field label={`${label}${REQUIRED.includes(k) ? " *" : ""}`} hint={props.hint}>
      <input data-testid={`book-${k}`} className="field" value={form[k]} onChange={set(k)} type={props.type || "text"} />
    </Field>
  );
  const reset = () => { setForm(EMPTY); setState({ loading: false, error: "", done: false }); };
  return (
    <PageShell eyebrow={t.book.eyebrow} title={t.book.title} desc={t.book.desc} testid="book">
      {state.done ? <Success t={t} form={form} reset={reset} /> : (
        <form onSubmit={submit} className="card p-6 sm:p-10 max-w-3xl grid sm:grid-cols-2 gap-5" data-testid="book-form">
          {input("name", t.common.name)}
          {input("phone", t.book.phone, { type: "tel" })}
          <div className="sm:col-span-2">{input("email", t.book.email, { type: "email" })}</div>
          {input("birth_date", t.common.date, { type: "date" })}
          {input("birth_time", t.common.time, { type: "time", hint: t.book.timeUnknown })}
          <div className="sm:col-span-2">{input("birth_place", t.common.place)}</div>
          <Field label={`${t.book.service} *`}>
            <select data-testid="book-service" className="field" value={form.service} onChange={set("service")}>
              <option value="">—</option>
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label={t.book.mode}>
            <select data-testid="book-mode" className="field" value={form.mode} onChange={set("mode")}>
              {Object.entries(t.book.modes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          {input("preferred_date", t.book.prefDate, { type: "date" })}
          {input("preferred_time", t.book.prefTime, { type: "time" })}
          <div className="sm:col-span-2">
            <Field label={t.book.questions}><textarea data-testid="book-questions" rows={4} className="field" value={form.questions} onChange={set("questions")} /></Field>
          </div>
          <div className="sm:col-span-2 space-y-4">
            <FormError msg={state.error} />
            <button type="submit" data-testid="book-submit" disabled={state.loading} className="pill-solid px-8 py-3.5 text-sm disabled:opacity-60">
              {state.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{state.loading ? t.book.sending : t.book.submit}
            </button>
          </div>
        </form>
      )}
    </PageShell>
  );
}
